/**
 * Project Guide card — V3 R4.
 *
 * "When a project starts, the guidance must live *in* the project, not in
 * chat." Every project is created with a pinned guide; this card nags for the
 * sections still holding their placeholder, and **disappears on its own** once
 * the guide has actually been written — so it is a prompt, not permanent
 * furniture.
 */

import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Check } from 'lucide-react';
import { PERMISSIONS } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { useCan } from '@/features/auth';

interface GuideState {
  exists: boolean;
  noteId: string | null;
  sections: string[];
  unfilled: string[];
}

export function useProjectGuide(projectId?: string) {
  return useQuery({
    queryKey: ['project-guide', projectId],
    enabled: Boolean(projectId),
    queryFn: async () => (await api.get<GuideState>(`/projects/${projectId}/guide`)).data,
  });
}

export function ProjectGuideCard({ projectId }: { projectId?: string }) {
  const { key } = useParams();
  const { data: guide } = useProjectGuide(projectId);
  const can = useCan();

  if (!guide?.exists) return null;
  // Fully written — the card has done its job and gets out of the way.
  if (guide.unfilled.length === 0) return null;

  const canEdit = can(PERMISSIONS.PROJECT_MANAGE_STATUSES);
  const done = guide.sections.length - guide.unfilled.length;

  return (
    <div className="vt-surface flex flex-wrap items-center gap-3 p-3" data-testid="project-guide-card">
      <BookOpen className="size-5 shrink-0 text-[var(--accent)]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium">Project Guide</span>
          <Badge tone="warn">
            {done}/{guide.sections.length} written
          </Badge>
        </div>
        <p className="text-[12px] vt-muted">
          Still to fill in: {guide.unfilled.join(', ')}.
          {canEdit ? '' : ' Ask the project lead to complete it.'}
        </p>
      </div>
      <Link to={`/projects/${key}/docs/${guide.noteId}`}>
        <Button variant="subtle" size="sm" data-testid="open-project-guide">
          <Check />
          {canEdit ? 'Finish the guide' : 'Read the guide'}
        </Button>
      </Link>
    </div>
  );
}
